import React, { useCallback, useEffect, useState } from 'react'
import { Home as IconHome, Info } from 'react-feather'
import { Link, useHistory } from 'react-router-dom'
import _ from 'lodash'
import * as yup from 'yup'
import styled from 'styled-components'
import { shade } from 'polished'
import useAxios from 'axios-hooks'
import { toast } from 'react-toastify'
import { Formik, Form, useFormikContext, Field as FormikField } from 'formik'
import {
  CardTitle,
  Input,
  Row,
  Button,
  CustomInput,
  UncontrolledTooltip,
  Modal,
  ModalBody,
} from 'reactstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { Col, LoadableCard } from '~/components/commons'
import Breadcrumb from '~/components/Breadcrumb'
import { validations } from './commons'
import { Field } from '../Posts/commons'
import UploadPhoto from '~/components/ecto/UploadPhoto'
import { ReactComponent as CheckIcon } from '~/assets/img/icons/check-icon.svg'
import { Alert } from '~/components/Alert'
import { ADMIN, CLIENT_ADMIN, SUPER_ADMIN } from '~/constants/roles'
import { ModulesTable } from '~/components/ModulesTable'
import { Drawer } from '~/components/Drawers'
import api from '~/services/api'

const initialValues = {
  user: {
    name: '',
    email: '',
    password: '',
    userPermission: {},
    permissions: {},
    mfaEnabled: false,
    userNotification: {},
  },
  passwordConfirmation: '',
  avatarImage: '',
}

const validationSchema = () =>
  yup.lazy(({ user }) =>
    yup.object().shape({
      user: !user.password ? validations.userWithoutPass : validations.user,
      passwordConfirmation: user.password
        ? validations.passwordConfirmation
        : null,
    })
  )

const FormContext = React.createContext()

const RESEND_MFA_TIMEOUT_IN_SEC = 120

function EditProfileForm() {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [isMfaEnable, setIsMfaEnable] = useState(false)
  const [codeDigit1, setCodeDigit1] = useState('')
  const [codeDigit2, setCodeDigit2] = useState('')
  const [codeDigit3, setCodeDigit3] = useState('')
  const [codeDigit4, setCodeDigit4] = useState('')
  const [codeDigit5, setCodeDigit5] = useState('')
  const [codeDigit6, setCodeDigit6] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(RESEND_MFA_TIMEOUT_IN_SEC)
  const [canResendMfa, setCanResendMfa] = useState(false)
  const [mfaToggleSuccess, setMfaToggleSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const userId = useSelector(state => state?.user?.profile?.id)
  const {
    setFieldValue,
    initialValues,
    values,
    handleSubmit,
  } = useFormikContext()

  const CODE_DIGIT_FUNCTION = [
    setCodeDigit1,
    setCodeDigit2,
    setCodeDigit3,
    setCodeDigit4,
    setCodeDigit5,
    setCodeDigit6,
  ]

  function startResendMfaTimeout() {
    setCanResendMfa(false)
    setSecondsLeft(RESEND_MFA_TIMEOUT_IN_SEC)
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          setCanResendMfa(true)
          clearInterval(interval)
          return RESEND_MFA_TIMEOUT_IN_SEC
        }
        return prev - 1
      })
    }, 1000)
  }

  async function handleResendMfa(event) {
    event.preventDefault()
    if (!canResendMfa) return
    try {
      setCanResendMfa(false)
      await api.post(`/users/${userId}/mfa/generate`)
      startResendMfaTimeout()
    } catch (error) {
      console.error(`Error on resend MFA`, error.response)
      toast.error(intl.formatMessage({ id: 'An unexpected error ocurred!' }))
    }
  }

  async function handleGenerateMfa(event) {
    event.preventDefault()
    try {
      setLoading(true)
      await api.post(`/users/${userId}/mfa/generate`)
      setCodeSent(true)
      startResendMfaTimeout()
    } catch (error) {
      console.error(`Error on generate MFA`, error.response)
      toast.error(intl.formatMessage({ id: 'An unexpected error ocurred!' }))
    }
    setLoading(false)
  }

  async function handleEnableMfa(event) {
    event.preventDefault()
    try {
      setLoading(true)
      await api.post(`/users/${userId}/mfa/enable`, {
        mfaToken: `${codeDigit1}${codeDigit2}${codeDigit3}${codeDigit4}${codeDigit5}${codeDigit6}`,
      })
      setFieldValue('user.mfaEnabled', true)
      setOpen(false)
      setCodeSent(false)
      setMfaToggleSuccess(true)
    } catch (error) {
      console.error(`Error on enable MFA`, error.response)
      if (error.response?.data?.error === 'Invalid or expired MFA token') {
        toast.error(
          intl.formatMessage({
            id: 'The code sent is either invalid or expired',
          })
        )
      } else {
        toast.error(intl.formatMessage({ id: 'An unexpected error ocurred!' }))
      }
    }
    setOpen(false)
  }

  async function handleDisableMfa(event) {
    event.preventDefault()
    try {
      setLoading(true)
      await api.post(`/users/${userId}/mfa/disable`, {
        mfaToken: `${codeDigit1}${codeDigit2}${codeDigit3}${codeDigit4}${codeDigit5}${codeDigit6}`,
      })
      setFieldValue('user.mfaEnabled', false)
      setOpen(false)
      setCodeSent(false)
      setMfaToggleSuccess(true)
    } catch (error) {
      console.error(`Error on enable MFA`, error.response)
      if (error.response?.data?.error === 'Invalid or expired MFA token') {
        toast.error(
          intl.formatMessage({
            id: 'The code sent is either invalid or expired',
          })
        )
      } else {
        toast.error(intl.formatMessage({ id: 'An unexpected error ocurred!' }))
      }
    }

    setLoading(false)
  }

  function handleClose() {
    setOpen(false)
    setCodeSent(false)
  }

  function handleCodeKeyDown(event, setValue) {
    const elem = event.target
    const value = event.key
    const isBackspace = event.key === 'Backspace'
    const isArrowLeft = event.key === 'ArrowLeft'
    const isArrowRight = event.key === 'ArrowRight'

    if (isBackspace) {
      setValue('')
      elem.value = ''
      const previousElem = elem.previousElementSibling
      if (previousElem) previousElem.focus()
      return
    }

    if (isArrowLeft) {
      const previousElem = elem.previousElementSibling
      if (previousElem) previousElem.focus()
      return
    }

    if (isArrowRight) {
      const nextElem = elem.nextElementSibling
      if (nextElem) nextElem.focus()
      return
    }

    const formattedValue = value.replace(/[^0-9]/g, '')
    if (formattedValue) setValue(formattedValue)

    const nextElem = elem.nextElementSibling
    if (nextElem && formattedValue) nextElem.focus()
  }

  function handlePaste(event) {
    const elem = event.target
    const paste = (event.clipboardData || window.clipboardData).getData('text')
    const formattedPaste = paste.replace(/[^0-9]/g, '')
    const digits = formattedPaste.split('')

    let nextElem = elem
    for (let i = 0; i < digits.length; i++) {
      if (i === 0) {
        CODE_DIGIT_FUNCTION[i](digits[i])
        continue
      }
      nextElem = nextElem.nextElementSibling
      if (!nextElem) break
      if (i === digits.length - 1) nextElem.focus()
      CODE_DIGIT_FUNCTION[i](digits[i])
    }
  }

  function getMinutesFromSeconds(secondsElapsed) {
    const minutes = Math.floor(secondsElapsed / 60)
    const seconds = secondsElapsed - minutes * 60

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  return (
    <>
      <Drawer open={open} onClose={handleClose}>
        <Drawer.Header>
          <Drawer.Header.Title>
            <FormattedMessage id={'Two factor authentication'} />
          </Drawer.Header.Title>
          <Drawer.Header.Close />
        </Drawer.Header>
        <Modal
          isOpen={mfaToggleSuccess}
          toggle={() => setMfaToggleSuccess(prevState => !prevState)}
          centered={true}
          size="md"
          backdrop={true}
        >
          <StyledModalBody>
            <div>
              <CheckIcon />
            </div>
            <div>
              <div className="modal-title">
                <FormattedMessage id="Success" />
              </div>
              <div className="modal-text">
                {isMfaEnable ? (
                  <FormattedMessage id="Two factor authentication successfully enabled." />
                ) : (
                  <FormattedMessage id="Two factor authentication successfully disabled." />
                )}
              </div>
            </div>
          </StyledModalBody>
        </Modal>
        {open && (
          <MFABody>
            {isMfaEnable ? (
              <FormattedMessage id="To enable the two factor authentication we need to confirm your email first." />
            ) : (
              <FormattedMessage id="To disable the two factor authentication we need to confirm your email first." />
            )}
            {codeSent ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <div className="code-label">
                    <FormattedMessage id="Enter your code:" />
                  </div>
                  <div className="code-input">
                    <input
                      id="codeDigit1"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit1}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit1)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                    <input
                      id="codeDigit2"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit2}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit2)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                    <input
                      id="codeDigit3"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit3}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit3)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                    <input
                      id="codeDigit4"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit4}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit4)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                    <input
                      id="codeDigit5"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit5}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit5)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                    <input
                      id="codeDigit6"
                      type="value"
                      minLength="1"
                      maxLength="1"
                      pattern="[0-9]"
                      value={codeDigit6}
                      onKeyDown={e => handleCodeKeyDown(e, setCodeDigit6)}
                      onPaste={e => handlePaste(e)}
                      placeholder="•"
                      disabled={loading}
                      required
                    ></input>
                  </div>
                </div>
                <div className="resend-div">
                  <button onClick={handleResendMfa} disabled={!canResendMfa}>
                    <FormattedMessage id="Resend code" />
                  </button>
                  {!canResendMfa && (
                    <div>{getMinutesFromSeconds(secondsLeft)}</div>
                  )}
                  <Info
                    id="info-mfa-tooltip"
                    width="1.25rem"
                    height="1.25rem"
                    color="#A0AEC0"
                  />
                  <UncontrolledTooltip target="info-mfa-tooltip">
                    <FormattedMessage id="Check your email spam folder before sending a new code." />
                  </UncontrolledTooltip>
                </div>
                <div className="button-menu">
                  <button
                    type="button"
                    className="code-button"
                    onClick={isMfaEnable ? handleEnableMfa : handleDisableMfa}
                    data-loading={loading}
                    disabled={
                      loading ||
                      !codeDigit1 ||
                      !codeDigit2 ||
                      !codeDigit3 ||
                      !codeDigit4 ||
                      !codeDigit5 ||
                      !codeDigit6
                    }
                  >
                    <FormattedMessage
                      id={
                        isMfaEnable
                          ? 'Enable two factor authentication'
                          : 'Disable two factor authentication'
                      }
                    />
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    <FormattedMessage id="Cancel" />
                  </button>
                </div>
              </>
            ) : (
              <div className="button-menu">
                <button
                  type="button"
                  className="code-button"
                  onClick={handleGenerateMfa}
                  data-loading={loading}
                  disabled={loading}
                >
                  <FormattedMessage id="Send code" />
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleClose}
                >
                  <FormattedMessage id="Cancel" />
                </button>
              </div>
            )}
          </MFABody>
        )}
      </Drawer>
      <Row className="mb-1">
        <Col md="6" className="px-1">
          <Field
            className="mb-1"
            name="user.name"
            label="Name"
            render={Input}
            required
          />
          <Field
            className="mb-1"
            name="user.email"
            label="Email"
            render={Input}
            type="email"
            required
          />
        </Col>
        <Col md="6" className="px-1">
          <UploadPhoto
            name="avatarImage"
            label="User photo"
            onChange={v => setFieldValue('avatarImage', v)}
            showHelperText
            helperText={`Allowed JPG or PNG. Max size of 800kB.`}
            helperText2={`This photo visible only in Locasty's dashboard.`}
            required
            maxSize={'110px'}
            initialImage={initialValues.user.avatar}
          />
        </Col>
      </Row>
      <Row>
        <Col md="6" className="px-1 mb-1">
          <Field
            className="mb-1"
            name="user.password"
            label={'New Password'}
            render={Input}
            type="password"
            required
            uuncontrolled="true"
          />
          <Field
            className="mb-1"
            name="passwordConfirmation"
            label={'Confirm New Password'}
            render={Input}
            type="password"
            required
            uncontrolled="true"
          />
        </Col>
        <Col
          md="6"
          className="px-1 mb-1 pt-2 d-flex flex-row"
          style={{ height: 'fit-content' }}
        >
          <Field
            name="user.userPermission.mfaEnabled"
            style={{ width: 'fit-content' }}
            required={false}
            render={() => (
              <CustomInput
                type="switch"
                id="user.userPermission.mfaEnabled"
                name="user.userPermission.mfaEnabled"
                checked={values.user.mfaEnabled}
                onChange={value => {
                  setIsMfaEnable(value.target.checked)
                  setOpen(true)
                }}
                inline
              />
            )}
            type="checkbox"
          />
          <label
            htmlFor="user.userPermission.mfaEnabled"
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: 'normal',
            }}
          >
            <FormattedMessage
              id={
                values?.user?.mfaEnabled
                  ? 'Disable two factor authentication'
                  : 'Enable two factor authentication'
              }
            />
          </label>
        </Col>
      </Row>
      <Row className="px-1 mb-2">
        <TwoColumnResponsive>
          <Col className="mb-1" style={{ minWidth: '50%' }}>
            <h3>
              <FormattedMessage id="Email Notifications" />
            </h3>
            <div className="custom-checkbox custom-control d-flex">
              <FormikField
                id="user.userNotification.monthlyReport"
                className="custom-control-input"
                type="checkbox"
                name="user.userNotification.monthlyReport"
              />
              <CheckBoxlabel
                className="custom-control-label"
                htmlFor="user.userNotification.monthlyReport"
              >
                <div style={{ fontSize: '1.2em', fontWeight: '500' }}>
                  <FormattedMessage id="notification.monthlyReport.title" />
                </div>
                <div style={{ fontSize: '1.2em', color: '#718096' }}>
                  <FormattedMessage id="notification.monthlyReport.description" />
                </div>
              </CheckBoxlabel>
            </div>
          </Col>
          <ModulesTable userId={userId} />
        </TwoColumnResponsive>
      </Row>
      <CreateButton type="submit" onClick={handleSubmit}>
        <FormattedMessage id={'Update Profile'} />
      </CreateButton>
    </>
  )
}

function UserProfile() {
  const [alertVisible, setAlertVisible] = useState(false)
  const history = useHistory()
  const [formData, setFormData] = useState(null)

  const userId = useSelector(state => state?.user?.profile?.id)

  const userProfile = useSelector(state => state?.user?.profile)
  const userPermission = userProfile?.userPermission

  const userIsAppAdmin = userProfile?.role === SUPER_ADMIN

  const userIsClientAdmin = userProfile?.role === CLIENT_ADMIN

  const userIsAdmin = userProfile?.role === ADMIN

  const [, updateProfile] = useAxios(
    {
      url: `/users/profile/${userId}`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const [, uploadFile] = useAxios(
    {
      url: '/upload',
      method: 'POST',
    },
    { manual: true }
  )

  const [, fetchUserData] = useAxios(`/users/${userId}`, { manual: true })

  const fillUserData = useCallback(async () => {
    const { data } = await fetchUserData()

    const user = {
      name: data.name,
      email: data.email,
      avatar: data.avatar,
      userPermission: data?.userPermission,
      permissions: data?.permissions,
      userNotification: data.userNotification,
      mfaEnabled: data?.mfaEnabled,
      password: '',
    }

    setFormData({
      user,
      passwordConfirmation: '',
    })
  }, [fetchUserData])

  useEffect(() => {
    if (userId) {
      fillUserData()
    }
  }, [userId, fillUserData])

  async function handleSubmit({ user, avatarImage }) {
    let uploadedImageLink

    if (avatarImage?.type) {
      const fd = new FormData()
      fd.append('file', avatarImage)
      await uploadFile({ data: fd })
        .then(({ data }) => {
          uploadedImageLink = data.uri
        })
        .catch(e => {
          toast.error('An error occurred during upload image! Try again later.')
          console.error(e)
          throw new Error()
        })
    }

    if (avatarImage === null) {
      uploadedImageLink = ''
    }

    if (uploadedImageLink === undefined) {
      uploadedImageLink = user.avatar
    }

    try {
      const formData = {
        ...user,
        avatar: uploadedImageLink,
      }

      await updateProfile({ data: formData })

      setAlertVisible(true)
    } catch (error) {
      console.error(`Error on update profile`, error.response)
      const { data } = error.response
      toast.error(data.error ?? data.message)
    }
  }

  return (
    <Col>
      <Breadcrumb
        className="mb-2"
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <IconHome size={20} />
              </Link>
            ),
          },
          { text: 'Update Profile' },
        ]}
      />

      <LoadableCard style={{ padding: '30px 40px' }}>
        <CardTitle>
          <FormattedMessage id={'Update Profile'} />
        </CardTitle>
        <FormContext.Provider
          value={{
            userIsAppAdmin,
            userIsClientAdmin,
            userIsAdmin,
            userPermission,
          }}
        >
          <Formik
            initialValues={formData ?? initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema()}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            enableReinitialize
          >
            {() => (
              <Form onSubmit={() => {}}>
                <EditProfileForm />
              </Form>
            )}
          </Formik>
        </FormContext.Provider>
      </LoadableCard>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => history.push('/')}
        confirmBtnText="Go to dashboard"
        onCancel={() => setAlertVisible(false)}
        hasCancelBtn={true}
        cancelBtnText="Close"
        body="Profile successfully updated!"
        title="Success"
      />
    </Col>
  )
}

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.75rem;
  padding: 3rem;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.78rem;

    .modal-title {
      color: #595959;
      font-size: 2.375rem;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    .modal-text {
      color: #6c6c6c;
      text-align: center;
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
    }
  }
`

const TwoColumnResponsive = styled.div`
  width: 100%;
  display: flex;
  gap: 1em;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const CreateButton = styled(Button)`
  font-size: 18px;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.color.primary} !important;
  margin-top: 30px;
  text-transform: uppercase;

  &:hover {
    background-color: ${props => shade(0.2, props.theme.color.primary)};
    color: white;
  }

  svg {
    margin-right: 12px;
  }
`

const CheckBoxlabel = styled.label`
  &:before {
    width: 1.3rem;
    height: 1.3rem;
  }

  &:after {
    width: 1.3rem;
    height: 1.3rem;
  }
`

const MFABody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  color: #2c2c2c;

  font-family: Montserrat;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;

  .button-menu {
    display: flex;
    flex-direction: column;
    gap: 0.5em;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.75rem 1rem;
      border-radius: 0.25rem;
    }

    button.code-button {
      background-color: #7367f0;
      border: none;
      color: #fff;
    }

    button.code-button:disabled {
      background-color: #d1d1d1;
      cursor: not-allowed;
    }

    button.code-button[data-loading='true'] {
      cursor: wait;
    }

    button.cancel-button {
      background: transparent;
      border: 1px solid #7367f0;
      color: #7367f0;
    }

    button.cancel-button:disabled {
      border: 1px solid #d1d1d1;
      color: #d1d1d1;
      cursor: not-allowed;
    }
  }

  .code-input {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 0.38rem;

    input {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.25rem;
      border: 1px solid #e2e8f0;
      background-color: #fff;

      color: rgba(0, 0, 0, 0.48);
      text-align: center;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      &:focus {
        outline: none;
        border-color: #7367f0;
      }
    }
  }

  .resend-div {
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;

    button {
      background-color: transparent;
      padding: 0;
      border: none;
      margin: 0;
      color: #718096;
      font-size: 1rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.5rem;
    }

    button:disabled {
      color: #d1d1d1;
      cursor: not-allowed;
    }

    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      border: 1px solid #e2e8f0;
      background: #f7fafc;

      color: #4a5568;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1rem;
    }
  }
`

export default UserProfile
